import {
	ListItem,
	Divider,
	IconButton,
	InputAdornment,
	Select,
	MenuItem,
} from '@mui/material';
import React from 'react';
import { Icon } from '../../../../blocks/icons';
import styles from './InvoiceEditPaymentInstallment.module.scss';
import rpcShared from '@rockpapercoin/rpc-shared';
import MuiTextFieldText from '../../../../mui/MuiTextField/MuiTextFieldText/MuiTextFieldText';
import { Button } from '../../../Button';
import {
	usePaymentInstallments,
	PaymentInstallmentDetails,
	OnUpdate,
} from '../../usePaymentInstallments';
import MuiCurrencyInput from '../../../../mui/MuiCurrencyInput/MuiCurrencyInput';
import MuiNumberInput from '../../../../mui/MuiNumberInput/MuiNumberInput';
import {
	InstallmentStatus,
	InvoiceEditPaymentInstallmentsInvoiceFragment,
	InvoiceEditPaymentInstallmentPaymentInstallmentFragment,
	InvoiceStatus,
} from '#types';
import { paymentInstallmentIsMutable } from '../helpers';
import { DatePicker } from '../../../DatePicker';
import { DocumentNode } from 'graphql';
import { gql } from '@apollo/client';
import { useDeviceDetection } from '../../../../hooks/useDeviceDetection';

const invoiceEditPaymentInstallmentPaymentInstallmentFragment = gql`
	fragment invoiceEditPaymentInstallmentPaymentInstallment on PaymentInstallment {
		id
		status
		name
		dueDate
		amountDue
		percentageDue
		enteredAsPercentageAmount
	}
`;

const invoiceEditPaymentInstallmentInvoiceFragment = gql`
	fragment invoiceEditPaymentInstallmentInvoice on Invoice {
		id
		paymentInstallments {
			...invoiceEditPaymentInstallmentPaymentInstallment
		}
	}
	${ invoiceEditPaymentInstallmentPaymentInstallmentFragment }
`;

export type InvoiceEditPaymentInstallmentProps = {
	invoice?: Omit<InvoiceEditPaymentInstallmentsInvoiceFragment, 'id'> & {
		id?: string;
	};
	paymentInstallment: InvoiceEditPaymentInstallmentPaymentInstallmentFragment;
	paymentInstallmentDetails?: PaymentInstallmentDetails[];
	onUpdate: OnUpdate;
	onRemove: ReturnType<
		typeof usePaymentInstallments
	>['onRemovePaymentInstallment'];
	onBlur?: () => void;
	newInstallmentName: string;
	somePaymentInstallmentsMutable: boolean;
	amountLabel: string;
};

const IfMutable: React.FC<{
	className?: string;
	status?: InstallmentStatus | null;
	children: React.JSX.Element;
	otherwise?: React.JSX.Element | null;
}> = ( {
	className,
	status,
	children,
	otherwise = <span className={ className }></span>,
} ) => ( paymentInstallmentIsMutable( { status } ) ? children : otherwise );

enum CalculationMethods {
	Percentage = 'Percentage',
	Dollar = 'Dollar',
}

type InvoiceEditPaymentInstallmentPropsWithFragment =
	React.FC<InvoiceEditPaymentInstallmentProps> & {
		fragments: {
			invoiceEditPaymentInstallmentInvoiceFragment: DocumentNode;
		};
	};

export const InvoiceEditPaymentInstallment: InvoiceEditPaymentInstallmentPropsWithFragment =
	( {
		invoice,
		paymentInstallment,
		paymentInstallmentDetails,
		onUpdate,
		onRemove,
		onBlur,
		newInstallmentName,
		somePaymentInstallmentsMutable,
		amountLabel,
	} ) => {
		const calculatedPaymentInstallment = invoice
			? rpcShared.invoices.calculateInstallmentToString(
				invoice,
				paymentInstallment,
				undefined,
				false
			  )
			: undefined;
		const existingInstallment = invoice?.paymentInstallments?.find(
			( pi ) => pi.id === paymentInstallment.id
		);

		const { isMobile } = useDeviceDetection();

		if ( !calculatedPaymentInstallment ) {
			return null;
		}

		const paymentInstallmentDetail = paymentInstallmentDetails?.find(
			( pi ) => pi.id === paymentInstallment.id
		);

		return (
			<>
				<ListItem
					disableGutters
					className={ [ styles.itemContainer, !somePaymentInstallmentsMutable ? styles.immutable : undefined, ].join( ' ' ) }
					data-cy={ paymentInstallment.id }
				>
					<MuiTextFieldText
						autoFocus={ paymentInstallmentDetail?.autoFocus || false }
						label={ isMobile ? 'Name' : undefined }
						value={ paymentInstallment.name || '' }
						disabled={ paymentInstallmentDetail?.disabled || false }
						errorText={ paymentInstallmentDetail?.validation[ 'name' ] }
						onChange={ ( e ) =>
							onUpdate( {
								name: typeof e.target.value === 'string' ? e.target.value : '',
							} )
						}
						onBlur={ () => {
							if ( !paymentInstallment.name ) {
								onUpdate( {
									name: existingInstallment?.name || newInstallmentName,
								} );
							}
							onBlur?.();
						} }
						slotProps={ { htmlInput: { 'data-cy': 'name' } } }
					/>
					<DatePicker
						label={ isMobile ? 'Due Date' : undefined }
						value={ paymentInstallment.dueDate }
						disabled={ paymentInstallmentDetail?.disabled || false }
						errorText={ paymentInstallmentDetail?.validation[ 'dueDate' ] }
						disablePast={
							!!invoice?.status && invoice.status !== InvoiceStatus.Draft
						}
						onChange={ ( dueDate ) => {
							onUpdate( { dueDate } );
						} }
						slotProps={ {
							textField: {
								onBlur,
								slotProps: { htmlInput: { 'data-cy': 'dueDate' } },
							},
						} }
						onAccept={ ( acceptedDate ) => {
							onUpdate( {
								dueDate: acceptedDate,
							} );
						} }
					/>
					<Select<CalculationMethods>
						disabled={ paymentInstallmentDetail?.disabled || false }
						value={
							paymentInstallment.enteredAsPercentageAmount
								? CalculationMethods.Percentage
								: CalculationMethods.Dollar
						}
						onChange={ ( e ) =>
							onUpdate( {
								enteredAsPercentageAmount:
									e.target.value === CalculationMethods.Percentage,
							} )
						}
					>
						<MenuItem value={ CalculationMethods.Dollar }>$</MenuItem>
						<MenuItem value={ CalculationMethods.Percentage }>%</MenuItem>
					</Select>
					<MuiCurrencyInput
						label={ isMobile ? amountLabel : undefined }
						value={ paymentInstallment.amountDue }
						disabled={
							paymentInstallmentDetail?.disabled ||
							false ||
							paymentInstallment.enteredAsPercentageAmount ||
							false
						}
						errorText={ paymentInstallmentDetail?.validation[ 'amountDue' ] }
						slotProps={ {
							input: { startAdornment: '$' },
							htmlInput: {
								'data-cy': 'amountDue',
								inputMode: 'decimal',
								onBlur,
							},
						} }
						onChange={ ( amountDue ) => onUpdate( { amountDue } ) }
					/>
					<MuiNumberInput
						label={ isMobile ? 'Percentage' : undefined }
						value={ paymentInstallment.percentageDue || 0 }
						disabled={
							paymentInstallmentDetail?.disabled ||
							false ||
							!paymentInstallment.enteredAsPercentageAmount
						}
						slotProps={ {
							htmlInput: { inputMode: 'decimal', 'data-cy': 'percentage' },
							input: {
								endAdornment: (
									<InputAdornment disableTypography position='start'>
										%
									</InputAdornment>
								),
							},
						} }
						onChange={ ( percentageDue ) => onUpdate( { percentageDue } ) }
						onBlur={ onBlur }
						max={ 100 }
						min={ 0 }
						limit={ /^\d{0,3}(\.\d{0,2})?$/ }
					/>
					<IfMutable
						status={ paymentInstallment.status }
						otherwise={ !somePaymentInstallmentsMutable ? null : undefined }
					>
						{ isMobile ? (
							<Button
								className={ styles.remove }
								onClick={ () => onRemove( paymentInstallment.id ) }
								variant='text'
								color='secondary'
								startIcon={
									<Icon
										type='trash'
										data-cy='payment-installment-edit-trash-button'
									/>
								}
							>
								Remove
							</Button>
						) : (
							<IconButton
								className={ styles.remove }
								onClick={ () => onRemove( paymentInstallment.id ) }
								data-cy='invoice-edit-payment-installment-remove'
							>
								<Icon
									type='trash'
									data-cy='payment-installment-edit-trash-button'
									className={ styles.icon }
								/>
							</IconButton>
						) }
					</IfMutable>
				</ListItem>
				<Divider />
			</>
		);
	};

InvoiceEditPaymentInstallment.fragments = {
	invoiceEditPaymentInstallmentInvoiceFragment,
};
